import React, { useMemo } from 'react';
import { useGetUserGroups, type UserGroup } from '@/api';
import { InterviewPoolPopover } from './InterviewPoolPopover';
import { InterviewPoolList } from './InterviewPoolList';

type InterviewerPoolConfigProps = {
  config?: {
    name?: string;
    label?: string;
    options?: UserGroup[];
    [key: string]: any;
  };
};

export const useInterviewerPoolConfig = ({
  config,
}: InterviewerPoolConfigProps = {}) => {
  const { name = 'interview_pool_id', ...overRides } = config ?? {};

  const { userGroups, isLoading } = useGetUserGroups();

  const poolOptions = useMemo(() => {
    return (userGroups ?? []).map((pool) => ({
      ...pool,
      value: pool.id,
      label: pool.name,
      rightElement: React.createElement(InterviewPoolPopover, {
        poolId: pool.id,
      }),
    }));
  }, [userGroups]);

  const poolSelectField = useMemo(
    () => ({
      type: 'select' as const,
      name,
      label: 'Add interviewer pool',
      tooltip:
        'This is a list of interviewer pools in Kula. You can choose one or add more by going to Settings -> Interviewer pools.',
      required: true,
      valueType: 'number' as const,
      options: poolOptions,
      placeholder: 'Select a pool',
      loading: isLoading,
      fieldProps: {
        menuPortalTarget: null,
        optionIndicator: 'checkbox',
      },
      condition: {
        operator: 'and',
        conditions: [
          { operator: 'equal', field: 'interview_type', value: 'one_on_one' },
          {
            operator: 'equal',
            field: 'interviewTypeRadio',
            value: 'interview_pool',
          },
        ],
      },
      ...overRides,
    }),
    [name, poolOptions, isLoading, overRides]
  );

  const poolMembersField = useMemo(
    () => ({
      type: 'custom' as const,
      render: (formMethods: {
        watch: (_name: string) => unknown;
        getValues: () => Record<string, unknown>;
      }) => {
        const interviewType = formMethods.watch('interview_type');
        const radioValue = formMethods.watch('interviewTypeRadio');
        const poolId = formMethods.watch(name);
        if (interviewType !== 'one_on_one' || radioValue !== 'interview_pool') {
          return null;
        }
        return React.createElement(InterviewPoolList, {
          value: poolId as number | string | null | undefined,
        });
      },
    }),
    [name]
  );

  return [poolSelectField, poolMembersField] as const;
};
